/**
 * Every change to the board is a small, serializable command. Commands are validated
 * (a bad one throws and leaves the board untouched), and History snapshots the board
 * before each one, so undo/redo is exact even when stacked parts settle.
 */
import { partDef } from '../parts/registry';
import { Board, footprint, type BoardJSON } from './board';
import type { PartProps, PartType, Placement, PropValue, Rot } from './types';

export type Command =
  | { op: 'place'; part: Placement }
  | { op: 'remove'; id: string }
  | { op: 'move'; id: string; x: number; y: number; rot: Rot }
  | { op: 'set'; id: string; key: string; value: PropValue }
  | { op: 'batch'; cmds: Command[] };

export class CommandError extends Error {}

export function apply(board: Board, cmd: Command): void {
  applyOne(board, cmd);
  // Elastic boards grow to keep room around the build (part of the same undoable step).
  board.grow();
}

function applyOne(board: Board, cmd: Command): void {
  switch (cmd.op) {
    case 'place': {
      if (board.parts.has(cmd.part.id)) throw new CommandError(`duplicate id ${cmd.part.id}`);
      const chk = board.check(cmd.part);
      if (!chk.ok) throw new CommandError(chk.problem);
      board.insert({ ...cmd.part, layer: chk.layer });
      return;
    }
    case 'remove': {
      if (!board.delete(cmd.id)) throw new CommandError(`no part ${cmd.id}`);
      return;
    }
    case 'move': {
      const p = board.parts.get(cmd.id);
      if (!p) throw new CommandError(`no part ${cmd.id}`);
      if (board.stackedOn(cmd.id).length) throw new CommandError('has-parts-on-top');
      const chk = board.check({ ...p, x: cmd.x, y: cmd.y, rot: cmd.rot }, cmd.id);
      if (!chk.ok) throw new CommandError(chk.problem);
      Object.assign(p, { x: cmd.x, y: cmd.y, rot: cmd.rot, layer: chk.layer });
      board.version++;
      board.settle();
      return;
    }
    case 'set': {
      const p = board.parts.get(cmd.id);
      if (!p) throw new CommandError(`no part ${cmd.id}`);
      const next = { ...p.props, [cmd.key]: cmd.value };
      // Only props that change the footprint (wire length, cable ends) need re-checking.
      // Flipping a switch must never lift it above the wires snapped onto it.
      const cells = (props: typeof next) =>
        footprint({ ...p, props })
          .map((c) => `${c.x},${c.y},${c.pin}`)
          .join(';');
      if (cells(next) !== cells(p.props)) {
        const chk = board.check({ ...p, props: next }, cmd.id);
        if (!chk.ok) throw new CommandError(chk.problem);
        p.layer = chk.layer;
      }
      p.props = next;
      board.version++;
      return;
    }
    case 'batch': {
      const before = board.toJSON();
      try {
        for (const c of cmd.cmds) applyOne(board, c);
      } catch (e) {
        restore(board, before);
        throw e;
      }
    }
  }
}

/** Replace the board's contents in place (keeps the same Board object). */
export function restore(board: Board, snap: BoardJSON): void {
  board.parts.clear();
  board.cols = snap.cols;
  board.rows = snap.rows;
  board.ox = snap.ox ?? 0;
  board.oy = snap.oy ?? 0;
  for (const p of snap.parts) board.parts.set(p.id, { ...p, props: { ...p.props } });
  board.version++;
}

/** Undo/redo. Transient changes (flipping a switch) can bypass history by calling `apply` directly. */
export class History {
  private undos: BoardJSON[] = [];
  private redos: BoardJSON[] = [];
  constructor(
    private board: Board,
    private limit = 100,
  ) {}

  run(cmd: Command): void {
    const before = this.board.toJSON();
    apply(this.board, cmd); // throws before we record anything
    this.undos.push(before);
    if (this.undos.length > this.limit) this.undos.shift();
    this.redos = [];
  }

  undo(): boolean {
    const snap = this.undos.pop();
    if (!snap) return false;
    this.redos.push(this.board.toJSON());
    restore(this.board, snap);
    return true;
  }

  redo(): boolean {
    const snap = this.redos.pop();
    if (!snap) return false;
    this.undos.push(this.board.toJSON());
    restore(this.board, snap);
    return true;
  }

  get canUndo() {
    return this.undos.length > 0;
  }
  get canRedo() {
    return this.redos.length > 0;
  }
  clear() {
    this.undos = [];
    this.redos = [];
  }
}

/** A new placement with the part's default props. */
export function makePart(
  board: Board,
  type: PartType,
  x: number,
  y: number,
  rot: Rot = 0,
  props: PartProps = {},
): Placement {
  return {
    id: board.newId(),
    type,
    x,
    y,
    rot,
    layer: 0,
    props: { ...partDef(type).defaultProps(), ...props },
  };
}
